import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building,
  Bot,
  RefreshCw,
  AlertCircle,
  FileText,
  Bell,
  CheckCircle,
  Clock
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';
import { getCompanyContext } from 'utils/setCompanyContext';

function extrairBotCob(payload, depth = 0) {
  if (payload == null || depth > 6) return null;

  if (typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.bot_cob && typeof payload.bot_cob === 'object') {
      return payload.bot_cob;
    }

    const nested = ['data', 'result', 'body', 'output', 'payload'];
    for (const key of nested) {
      if (payload[key] != null && typeof payload[key] === 'object') {
        const found = extrairBotCob(payload[key], depth + 1);
        if (found) return found;
      }
    }
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const found = extrairBotCob(item, depth + 1);
      if (found) return found;
    }
  }

  return null;
}

function formatarValorCampo(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : String(raw);
}

const CARDS_BOT_COB = [
  {
    key: 'boletos_registrados',
    titulo: 'Boletos registrados',
    icon: FileText,
    cor: 'text-blue-600'
  },
  {
    key: 'notificacoes_enviadas',
    titulo: 'Notificações enviadas',
    icon: Bell,
    cor: 'text-purple-600'
  },
  {
    key: 'boletos_pagos_cadastrados',
    titulo: 'Boletos pagos cadastrados',
    icon: CheckCircle,
    cor: 'text-emerald-600'
  },
  {
    key: 'boletos_pendentes',
    titulo: 'Boletos pendentes',
    icon: Clock,
    cor: 'text-amber-600'
  }
];

const MovFibraBotCobrancaDashboard = ({ action = 'auto-selected', refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const ctx = getCompanyContext();
      const params = { action };
      if (ctx?.companies?.length === 1 && ctx.companies[0]?.name) {
        params.empresa = ctx.companies[0].name;
      }

      const response = await api.get('/automacoes/dashboard', { params });
      const payload = response?.data?.data ?? response.data;
      setDashboardData(payload);
    } catch (err) {
      console.error('Erro ao carregar BOT COBRANÇA MOV Fibra:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  const botCob = useMemo(() => extrairBotCob(dashboardData), [dashboardData]);

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando BOT COBRANÇA"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button
          type="button"
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              <Building className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">MOV Fibra</h1>
              <p className="text-[color:var(--text-muted)]">
                Dashboard de automações da MOV Fibra — BOT COBRANÇA
              </p>
            </div>
          </div>
          <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg hidden sm:block">
            <Bot className="h-6 w-6 text-[color:var(--accent)]" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <Bot className="h-5 w-5 mr-2 text-[color:var(--accent)]" />
          BOT COBRANÇA
        </h3>

        {botCob ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {CARDS_BOT_COB.map(({ key, titulo, icon: Icon, cor }) => {
              const valor = formatarValorCampo(botCob[key]);
              return (
                <div
                  key={key}
                  className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
                      <Icon className={`h-6 w-6 ${cor}`} />
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className="text-sm font-medium text-[color:var(--text-muted)]">{titulo}</p>
                      <p className="text-2xl font-bold text-[color:var(--text-primary)] tabular-nums mt-1">
                        {valor !== null
                          ? typeof valor === 'number'
                            ? valor.toLocaleString('pt-BR')
                            : valor
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-6 text-center">
            <Bot className="h-8 w-8 text-[color:var(--text-muted)] mx-auto mb-2" />
            <p className="text-sm text-[color:var(--text-muted)]">
              Nenhum dado de BOT COBRANÇA disponível no webhook.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovFibraBotCobrancaDashboard;
